/**
 * Editorial content for the Upcoming IPO pages (/upcoming-ipos,
 * /sme-upcoming-ipos): the DRHP-to-listing journey steps and FAQs. Filings
 * and schedules come from the IPO APIs.
 */

export const DRHP_JOURNEY_STEPS = [
  {
    title: 'DRHP Submitted',
    body: 'The company files its Draft Red Herring Prospectus with SEBI and the stock exchanges. It stays open for 21 days of public comments.',
    tag: 'Public inspection',
  },
  {
    title: 'Under Process & Queries',
    body: 'SEBI scrutinises disclosures, financial metrics and legal compliance, and lead managers respond to regulatory queries.',
    tag: 'Avg 30–90 days',
  },
  {
    title: 'Observation Issued (Approval)',
    body: 'SEBI issues its final observation letter. The issuer then has a 12-month window to launch the IPO.',
    tag: '1-year launch validity',
    highlight: true,
  },
  {
    title: 'RHP & Bidding Window',
    body: 'The Red Herring Prospectus is filed with the Registrar of Companies, the price band is announced and bidding dates are fixed.',
    tag: 'Trading & listing',
  },
];

export const UPCOMING_FAQS = [
  {
    question: 'What are the next upcoming IPOs?',
    answer:
      'Upcoming IPOs are companies expected to launch their public issues soon, based on their regulatory filings and exchange approvals. This tracker lists each one with its DRHP filing date, approval status, issue size split, lead managers and the exchange where it plans to list, whether NSE, BSE or an SME platform.',
  },
  {
    question: 'How to find upcoming IPOs?',
    answer:
      'Start with the "SEBI Approved Queue" tab: these companies have their observation letter and can open bidding at any time within 12 months. Companies still under review, or on the confidential route, are further away from launch.',
  },
  {
    question: 'How to track upcoming IPOs?',
    answer:
      'Star any company to add it to your watchlist on this device, export the list as CSV, or follow the IPO calendar once bidding dates are announced in the Red Herring Prospectus.',
  },
  {
    question: 'How to buy upcoming IPOs?',
    answer:
      'You can apply only once the bidding window opens. Use UPI through your broker app, or ASBA through net banking, and approve the UPI mandate before 5:00 PM on the closing day.',
  },
  {
    question: 'Which are the upcoming IPOs?',
    answer:
      'Every company in the table above has filed a DRHP. Those marked "SEBI Approved" are closest to launch; the rest are still in regulatory review or have filed confidentially.',
  },
  {
    question: 'What are the best upcoming IPOs?',
    answer:
      'We don’t rank or recommend issues. Compare each company’s financials, valuation against listed peers, use of proceeds and risk factors in its prospectus, and consult a registered adviser before investing.',
  },
  {
    question: 'Is there a list of upcoming IPOs?',
    answer:
      'Yes — the table above is that list. Use the tabs to switch between Mainboard, SME, confidential filings and the SEBI-approved queue, and the filters to narrow it by status, sector or lead manager.',
  },
];

// Upcoming SME IPOs page (/sme-upcoming-ipos). SME offer documents are filed
// with the SME platform of the exchange, which vets them in place of SEBI.
export const SME_JOURNEY_STEPS = [
  {
    title: 'DRHP Filed with the Exchange',
    body: 'The company files its Draft Red Herring Prospectus with NSE Emerge or BSE SME, and the draft is published for public comments.',
    tag: 'Public inspection',
  },
  {
    title: 'Exchange Review & Queries',
    body: "The exchange's SME platform checks eligibility, disclosures and financials, and the lead manager answers its queries.",
    tag: 'Exchange scrutiny',
  },
  {
    title: 'In-Principle Approval',
    body: 'The exchange grants in-principle approval to list, clearing the company to finalise its offer document and issue timetable.',
    tag: 'Cleared to launch',
    highlight: true,
  },
  {
    title: 'RHP & Bidding Window',
    body: 'The Red Herring Prospectus is filed with the Registrar of Companies, the price band and lot size are announced and bidding dates are fixed.',
    tag: 'Bidding & listing',
  },
];

export const SME_UPCOMING_FAQS = [
  {
    question: 'What are upcoming SME IPOs?',
    answer:
      'Small and medium enterprises planning to list on NSE Emerge or BSE SME. This page lists the SME issues whose bidding dates are already confirmed, with price band, lot size and issue size for each.',
  },
  {
    question: 'Where is an SME IPO draft prospectus filed?',
    answer:
      'With the SME platform of the stock exchange where the company wants to list — NSE Emerge or BSE SME. The exchange reviews the draft and grants in-principle approval, rather than SEBI issuing an observation letter as it does for mainboard IPOs.',
  },
  {
    question: 'How do I apply for an upcoming SME IPO?',
    answer:
      'Once bidding opens, apply through your broker app with UPI or through net-banking ASBA. SME bids are placed in whole lots, so check the per-lot amount first, and approve the UPI mandate before 5:00 PM on the closing day.',
  },
  {
    question: 'Are SME IPOs riskier than mainboard IPOs?',
    answer:
      'They carry different risks: smaller companies, larger minimum lots and thinner trading after listing, which can make it harder to exit. We don’t rank or recommend issues — read the RHP and consult a registered adviser before investing.',
  },
  {
    question: 'Is there a list of upcoming SME IPOs?',
    answer:
      'Yes — the "SME IPOs Opening Soon" table above lists every SME issue with confirmed bidding dates. Use the status filter to separate issues that are already open from those opening in the coming days.',
  },
];
